import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Slider from "react-slick";
import api from '../../api/axiosConfig';
import { ProductImageGallery } from '../../components/product/ProductImageGallery';
import PDFViewer from "../../components/PDFViewer";


// --- Helper: tạo URL tuyệt đối an toàn ---
const createAbsoluteUrl = (path?: string | null): string => {
  if (!path) return '';

  // ✅ Nếu là URL Cloudinary hoặc đã là URL tuyệt đối → giữ nguyên
  if (/^https?:\/\//i.test(path) || path.includes("res.cloudinary.com")) {
    return path;
  }

  // ✅ Nếu là đường dẫn local (dev uploads) → nối với base URL
  const base = (api.defaults.baseURL || process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '');
  return `${base}${path.startsWith('/') ? path : '/' + path}`;
};

// --- Interface dữ liệu ---
interface ProductDetailData {
  id?: string;
  slug?: string;
  name_vi?: string;
  name_en?: string;
  description_vi?: string;
  description_en?: string;
  mainImage?: string;
  galleryImages?: string[];
  short_features_vi?: string[];
  short_features_en?: string[];
  features_vi?: string[];
  features_en?: string[];
  volumes?: string[];
  tabs?: {
    product_info?: {
      title_vi: string;
      title_en: string;
      content: {
        benefits_vi?: string[];
        benefits_en?: string[];
        features_vi?: string[];
        features_en?: string[];
        image?: string;
        imageId?: string;
      };
    };
    usage_guide?: {
      title_vi: string;
      title_en: string;
      content: {
        method1_vi?: { title: string; steps: string[] };
        method1_en?: { title: string; steps: string[] };
        method2_vi?: { title: string; steps: string[] };
        method2_en?: { title: string; steps: string[] };
        video?: string;
      };
    };
    tech_specs?: {
      title_vi: string;
      title_en: string;
      content: {
        specs_vi?: string[];
        specs_en?: string[];
        pdf?: string;
        pdfId?: string;       // ✅ thêm
      };
    };
  };
  faq_vi?: Array<{ question: string; answer: string }>;
  faq_en?: Array<{ question: string; answer: string }>;
}

// --- Icon mũi tên xuống ---
const ChevronDownIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
    strokeWidth={2} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
  </svg>
);

// --- Component: Sản phẩm liên quan ---
const RelatedProductsCarousel = ({ currentProductId }: { currentProductId: string }) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const [relatedProducts, setRelatedProducts] = useState<any[]>([]);

  useEffect(() => {
    const fetchRelated = async () => {
      try {
        const { data } = await api.get(`/products?lang=${currentLang}`);
        setRelatedProducts(
          (data || []).filter(
            (p: any) => (p.id || p.slug) && p.id !== currentProductId && p.slug !== currentProductId
          )
        );
      } catch (error) {
        console.error('❌ Lỗi tải sản phẩm liên quan:', error);
      }
    };
    fetchRelated();
  }, [currentLang, currentProductId]);

  const settings = {
    dots: true,
    infinite: relatedProducts.length > 3,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 2 } },
      { breakpoint: 640, settings: { slidesToShow: 1 } },
    ],
  };

  return (
    <div className="related-products-slider">
      <Slider {...settings}>
        {relatedProducts.map((prod) => {
          const slugOrId = prod.slug || prod.id;
          const displayName =
            currentLang === 'vi'
              ? prod.name_vi || prod.name || prod.name_en
              : prod.name_en || prod.name || prod.name_vi;

          const description =
            currentLang === 'vi'
              ? prod.description_vi || prod.description
              : prod.description_en || prod.description;

          return (
            <div key={slugOrId} className="px-2 md:px-3">
              <div className="bg-white overflow-hidden h-full flex flex-col group transition-all duration-300 hover:-translate-y-1 rounded-md shadow">
                <Link to={`/${currentLang}/products/${slugOrId}`} className="block h-48 bg-gray-100 p-2">
                  {prod.mainImage ? (
                    <img
                      src={createAbsoluteUrl(prod.mainImage)}
                      alt={displayName}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      No Image
                    </div>
                  )}
                </Link>
                <div className="p-4 flex flex-col flex-grow">
                  <h4 className="text-lg font-bold text-gray-800 truncate">
                    <Link
                      to={`/${currentLang}/products/${slugOrId}`}
                      className="hover:text-[#4A6C35]"
                    >
                      {displayName}
                    </Link>
                  </h4>
                  <p className="text-sm text-gray-500 mt-1 flex-grow">
                    {description ? description.substring(0, 60) + '...' : ''}
                  </p>
                  <Link
                    to={`/${currentLang}/products/${slugOrId}`}
                    className="mt-4 w-full text-center bg-gray-100 text-[#4A6C35] font-semibold py-2 group-hover:bg-[#4A6C35] group-hover:text-white transition-colors duration-300"
                  >
                    {t('products_page.view_details_button')}
                  </Link>
                </div>
              </div>
            </div>
          );
        })}
      </Slider>
    </div>
  );
};


// --- Component chính: ProductDetail ---
const ProductDetail = () => {
  const { productId } = useParams<{ productId: string }>();
  const { t, i18n } = useTranslation();
  const lang = i18n.language as 'vi' | 'en';

  const [product, setProduct] = useState<ProductDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('product_info');
  const [selectedVolume, setSelectedVolume] = useState('');
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/products/${productId}`);
        setProduct(data);
        if (data.volumes?.[0]) setSelectedVolume(data.volumes[0]);
        document.title = data[`name_${lang}`] || data.name_vi || 'Chi tiết sản phẩm';
      } catch (err) {
        console.error(`❌ Lỗi tải sản phẩm ${productId}:`, err);
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };
    if (productId) fetchProduct();
    window.scrollTo(0, 0);
  }, [productId, lang]);

  if (loading)
    return <div className="flex justify-center items-center h-screen font-semibold text-lg">Đang tải chi tiết sản phẩm...</div>;

  if (!product)
    return (
      <div className="min-h-screen flex justify-center items-center bg-white">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800">Không tìm thấy sản phẩm</h2>
          <p className="text-gray-500 mt-2">Sản phẩm bạn đang tìm kiếm không tồn tại.</p>
        </div>
      </div>
    );

  const galleryUrls = (product.galleryImages || []).map(createAbsoluteUrl);
  const tabKeys = Object.keys(product.tabs || {}) as Array<keyof NonNullable<typeof product.tabs>>;

  return (
    <div className="bg-white">
      {/* --- Hero section --- */}
      <section className="bg-gray-50 py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <ProductImageGallery
            images={galleryUrls}
            productName={product[`name_${lang}`] || product.name_vi || product.name_en || ''}
          />
          <div>
            <h1 className="text-4xl md:text-5xl font-extrabold text-[#2C4420]">
              {product[`name_${lang}`] || product.name_vi || product.name_en || 'No name'}
            </h1>
            <div className="flex flex-wrap gap-2 mt-4">
              {[...(product[`short_features_${lang}`] || []), ...(product[`features_${lang}`] || [])].map((f, i) => (
                <span key={i} className="text-sm font-semibold bg-[#DBF8C8] text-green-800 px-3 py-1 rounded">
                  {f}
                </span>
              ))}
            </div>
            <p className="mt-6 text-lg text-gray-600 leading-relaxed">
              {product[`description_${lang}`] || product.description_vi || product.description_en || ''}
            </p>

            {product.volumes?.length ? (
              <div className="mt-8 p-6 bg-white border border-gray-200 rounded-lg">
                <h3 className="text-md font-semibold text-gray-800">{t('product_detail.select_volume')}:</h3>
                <div className="flex flex-wrap gap-3 mt-3">
                  {product.volumes.map((v) => (
                    <button
                      key={v}
                      onClick={() => setSelectedVolume(v)}
                      className={`px-4 py-2 font-semibold text-sm transition-all duration-200 ${
                        selectedVolume === v
                          ? 'bg-[#658C4A] text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                      }`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </section>

      {/* --- Tabs section --- */}
      <section className="py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center border-b border-gray-200 mb-12">
            {tabKeys.map((k) => (
              <button
                key={k}
                onClick={() => setActiveTab(k)}
                className={`py-3 px-4 font-semibold border-b-4 ${
                  activeTab === k
                    ? 'border-[#4A6C35] text-[#4A6C35]'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                {product.tabs?.[k]?.[`title_${lang}`]}
              </button>
            ))}
          </div>

          <div className="min-h-[300px]">
            {/* Tab: Thông tin sản phẩm */}
            {activeTab === 'product_info' && product.tabs?.product_info && (
              <div className="space-y-12 animate-fade-in">
                <div className="grid md:grid-cols-2 gap-10 text-gray-700">
                  <div>
                    <h3 className="text-2xl font-bold mb-5 border-l-4 border-[#4A6C35] pl-4">
                      {t('product_detail.tabs.product_info.benefits')}
                    </h3>
                    <ul className="list-disc list-inside space-y-2">
                      {(product.tabs.product_info.content[`benefits_${lang}`] || []).map((b, i) => (
                        <li key={i}>{b}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold mb-5 border-l-4 border-[#4A6C35] pl-4">
                      {t('product_detail.tabs.product_info.features')}
                    </h3>
                    <ul className="list-disc list-inside space-y-2">
                      {(product.tabs.product_info.content[`features_${lang}`] || []).map((f, i) => (
                        <li key={i}>{f}</li>
                      ))}
                    </ul>
                  </div>
                </div>
                {product.tabs.product_info.content.image && (
                  <div className="mt-12 flex justify-center">
                    <img
                      src={createAbsoluteUrl(product.tabs.product_info.content.image)}
                      alt="product-info"
                      className="w-full max-w-4xl h-auto object-cover"
                    />
                  </div>
                )}
              </div>
            )}

            {activeTab === 'usage_guide' && product.tabs?.usage_guide && (
              <div className="space-y-12 animate-fade-in">
                {(['method1', 'method2'] as const).map((key) => {
                  const content = product.tabs!.usage_guide!.content as {
                    method1_vi?: { title: string; steps: string[] };
                    method1_en?: { title: string; steps: string[] };
                    method2_vi?: { title: string; steps: string[] };
                    method2_en?: { title: string; steps: string[] };
                    video?: string;
                  };

                  const method = content[`${key}_${lang}` as keyof typeof content] as
                    | { title: string; steps: string[] }
                    | undefined;

                  if (!method) return null;

                  return (
                    <div key={key}>
                      <h3 className="text-2xl font-bold mb-4">{method.title}</h3>
                      <ul className="list-disc list-inside space-y-2">
                        {(method.steps || []).map((s: string, i: number) => (
                          <li key={i}>{s}</li>
                        ))}
                      </ul>
                    </div>
                  );
                })}

                {/* Video hướng dẫn */}
                {product.tabs.usage_guide.content.video && (
                  <div className="aspect-w-16 aspect-h-9">
                    <iframe
                      src={product.tabs.usage_guide.content.video.replace('watch?v=', 'embed/')}
                      className="w-full h-full"
                      allowFullScreen
                      title="Usage video"
                    />
                  </div>
                )}
              </div>
            )}


            {/* Tab: Thông số kỹ thuật */}
            {/* Tab: Thông số kỹ thuật */}
            {activeTab === 'tech_specs' && product.tabs?.tech_specs && (
              <div className="bg-gray-50 p-6 border text-gray-700 space-y-4 rounded-md animate-fade-in">
                {/* Danh sách thông số kỹ thuật */}
                <ul className="list-disc list-inside space-y-2">
                  {(product.tabs.tech_specs.content[`specs_${lang}`] || []).map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>

                {/* Hiển thị file PDF nếu có */}
                {product.tabs.tech_specs.content.pdf && (
                  <div className="mt-8 space-y-6">
                    <h4 className="text-lg font-semibold text-gray-800 text-center">
                      📘 {lang === "vi" ? "Xem trực tiếp tài liệu kỹ thuật" : "View technical document"}
                    </h4>

                    <PDFViewer url={createAbsoluteUrl(product.tabs.tech_specs.content.pdf)} />

                    <div className="text-center">
                      <a
                        href={createAbsoluteUrl(product.tabs.tech_specs.content.pdf)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline text-sm"
                      >
                        {lang === "vi" ? "Tải xuống file PDF" : "Download PDF"}
                      </a>
                    </div>
                  </div>
                )}

                {/* Trường hợp chưa có PDF */}
                {!product.tabs.tech_specs.content.pdf && (
                  <p className="text-center text-gray-500 italic mt-4">
                    {lang === 'vi'
                      ? 'Chưa có file PDF cho sản phẩm này.'
                      : 'No technical PDF file available for this product.'}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* --- FAQ section --- */}
      <section className="bg-gray-50 py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900">{t('product_detail.faq.title')}</h2>
            <p className="mt-4 text-lg text-gray-600">{t('product_detail.faq.subtitle')}</p>
          </div>
          <div className="space-y-4">
            {(product[`faq_${lang}`] || []).map((item, i) => (
              <div key={i} className="border rounded-md">
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === i ? null : i)}
                  className="w-full flex justify-between items-center text-left p-5 bg-white hover:bg-gray-50"
                >
                  <span className="text-lg font-semibold text-gray-800">{item.question}</span>
                  <ChevronDownIcon
                    className={`w-6 h-6 text-gray-500 transition-transform ${openFaqIndex === i ? 'rotate-180' : ''}`}
                  />
                </button>
                <div className={`transition-all duration-500 ${openFaqIndex === i ? 'max-h-[500px]' : 'max-h-0 overflow-hidden'}`}>
                  <div
                    className="p-5 border-t bg-white text-gray-700 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: item.answer }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- Sản phẩm liên quan --- */}
      <section className="bg-gray-50 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto">
          <h3 className="text-3xl font-extrabold mb-8">{t('product_detail.related_products.title')}</h3>
          <RelatedProductsCarousel currentProductId={productId!} />
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;
